import {formatMoney} from '@shopify/theme-currency';

import FetchError from '../util/fetch-error';
import slideDown from '../util/slide-down';
import slideUp from '../util/slide-up';

import {CartNotes} from './cart-notes';
import {ShippingCalculator} from './cart-shipping';
import {CartShippingMessage} from './cart-shipping-message';
import {ProductAddButton} from './product-add';
import {initQtySection} from './quantity-selector';

const selectors = {
  drawer: '[data-drawer="drawer-cart"]',
  shipping: '[data-shipping-estimate-form]',
  loader: '[data-cart-loading]',
  form: '[data-cart-form]',
  emptystate: '[data-cart-empty]',
  items: '[data-line-items]',
  subtotal: '[data-cart-subtotal]',
  bottom: '[data-cart-bottom]',
  quantity: '[data-quantity-selector]',
  errors: '[data-form-errors]',
  item: '[data-cart-item]',
  finalPrice: '[data-cart-final]',
  key: 'data-update-cart',
  remove: 'data-remove-key',
  upsellProduct: '[data-upsell-holder]',
  cartPage: '[data-section-type="cart"]',
};

const classes = {
  hidden: 'cart--hidden',
  loading: 'cart--loading',
};

class CartItems {
  constructor(section) {
    this.container = section.container;
    this.drawer = this.container.querySelector(selectors.drawer);
    this.form = this.container.querySelector(selectors.form);
    this.loader = this.container.querySelector(selectors.loader);
    this.bottom = this.container.querySelector(selectors.bottom);
    this.items = this.container.querySelector(selectors.items);
    this.subtotal = this.container.querySelector(selectors.subtotal);
    this.errors = this.container.querySelector(selectors.errors);
    this.finalPrice = this.container.querySelector(selectors.finalPrice);
    this.emptystate = this.container.querySelector(selectors.emptystate);
    this.latestClick = null;
    this.cart = null;
    this.stale = true;
    this.cartPage = document.querySelector(selectors.cartPage);
    this.listen();
  }

  listen() {
    document.addEventListener(
      'theme:cart:change',
      function (event) {
        this.cart = event.detail.cart;
        this.stale = true;
      }.bind(this)
    );

    document.addEventListener(
      'theme:cart:init',
      function () {
        this.init();
      }.bind(this)
    );

    document.addEventListener(
      'theme:cart:reload',
      function () {
        this.stale = true;
        if (this.cart) {
          this.loadHTML();
        } else {
          this.init().then(() => this.loadHTML());
        }
      }.bind(this)
    );

    if (this.drawer) {
      this.drawer.addEventListener(
        'theme:drawer:open',
        function () {
          if (this.cart) {
            this.loadHTML();
          } else {
            this.init().then(() => this.loadHTML());
          }
        }.bind(this)
      );
    }

    new CartNotes(this.container);
    new CartShippingMessage(this.container);
  }

  init() {
    return window
      .fetch(`${window.theme.routes.cart}.js`)
      .then(this.handleErrors)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.cart = response;
        this.fireChange(response);
        return response;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  loadHTML() {
    if (this.stale) {
      if (this.cart && this.cart.item_count > 0) {
        this.loadForm();
      } else {
        this.showEmpty();
      }
    }
    this.stale = false;
  }

  initInputs() {
    this.inputs = this.container.querySelectorAll(`[${selectors.key}]`);
    this.inputs.forEach((input) => {
      const key = input.getAttribute(selectors.key);
      input.addEventListener(
        'change',
        function (e) {
          const quantity = parseInt(e.target.value, 10);
          this.latestClick = e.target.closest(selectors.item);
          this.lockState();
          this.updateCart(key, quantity);
        }.bind(this)
      );
    });
  }

  initRemove() {
    this.removers = this.container.querySelectorAll(`[${selectors.remove}]`);
    this.removers.forEach((remover) => {
      const key = remover.getAttribute(selectors.remove);
      remover.addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          this.latestClick = e.target.closest(selectors.item);
          this.lockState();
          this.updateCart(key, 0);
        }.bind(this)
      );
    });
  }

  lockState() {
    this.latestClick.querySelector('.item--loadbar').style.display = 'block';
    this.loader.classList.add(classes.loading);
  }

  updateCart(clickedKey, newQuantity) {
    let oldCount = null;
    let newCount = null;
    let newItem = null;
    window
      .fetch(`${window.theme.routes.cart}.js`)
      .then(this.handleErrors)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        const matchKeys = (item) => item.key === clickedKey;
        const index = response.items.findIndex(matchKeys);
        oldCount = response.item_count;
        newItem = response.items[index].title;
        const data = {
          line: `${index + 1}`,
          quantity: newQuantity,
        };
        return window.fetch(`${window.theme.routes.cart}/change.js`, {
          method: 'post',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data),
        });
      })
      .then(this.handleErrors)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        this.cart = response;
        newCount = response.item_count;
        if (oldCount === newCount) {
          this.stockoutError(newItem);
          this.stale = true;
        } else {
          slideUp(this.errors);
          this.fireChange(response);
          this.stale = true;
        }

        this.loadHTML();
      })
      .catch((e) => {
        console.error(e);
        let heading = '';
        if (typeof e.status !== 'undefined') {
          heading = `<p>${e.status}</p>`;
        }
        let paragraph = e.json.description || '';
        this.showError(`${heading + paragraph}`);
        this.loadHTML();
      });
  }

  fireChange(newCart) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: {
          cart: newCart,
        },
        bubbles: true,
      })
    );
  }

  updateTotal() {
    if (this.cart && this.cart.total_price) {
      const price = formatMoney(this.cart.total_price, theme.moneyFormat);
      this.finalPrice.innerHTML = price;
    }
    if (this.subtotal && this.cart) {
      window
        .fetch(`${window.theme.routes.root_url}?section_id=api-cart-subtotal`)
        .then(this.handleErrors)
        .then((response) => {
          return response.text();
        })
        .then((response) => {
          const fresh = document.createElement('div');
          fresh.innerHTML = response;
          this.subtotal.innerHTML = fresh.querySelector('[data-api-content]').innerHTML;
        });
    }
  }

  showError(message) {
    slideUp(this.errors);
    this.errors.innerHTML = message;
    window.setTimeout(() => {
      slideDown(this.errors);
    }, 600);
  }

  stockoutError(itemTitle) {
    let heading = `<p><strong>${window.theme.strings.stockout}</strong></p>`;
    let paragraph = `<p>${itemTitle}</p>`;
    this.showError(`${heading + paragraph}`);
  }

  loadForm() {
    window
      .fetch(`${window.theme.routes.root_url}?section_id=api-cart-items`)
      .then(this.handleErrors)
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const fresh = document.createElement('div');
        fresh.innerHTML = response;
        this.items.innerHTML = fresh.querySelector('[data-api-content]').innerHTML;

        this.showForm();
        this.initQuantity();
        this.initUpsell();
        this.updateTotal();
      });
  }

  initUpsell() {
    const upsellProduct = this.items.querySelector(selectors.upsellProduct);
    const oldUpsellProduct = this.bottom.querySelector(selectors.upsellProduct);
    const upsellButton = this.items.querySelector('[data-add-action-wrapper]');

    if (oldUpsellProduct) {
      oldUpsellProduct.remove();
    }

    if (this.cartPage && upsellProduct) {
      this.bottom.insertBefore(upsellProduct, this.bottom.firstChild);
    }

    if (upsellProduct && upsellButton) {
      // isCartItem tells add button to refresh the cart
      // instead of loading a popdown notification
      const isCartItem = true;
      new ProductAddButton(upsellButton, isCartItem);
    }
  }

  initQuantity() {
    initQtySection(this.container);
    this.initInputs();
    this.initRemove();
  }

  showForm() {
    this.form.classList.remove(classes.hidden);
    this.bottom.classList.remove(classes.hidden);
    this.loader.classList.remove(classes.loading);
    this.emptystate.classList.add(classes.hidden);
  }

  showEmpty() {
    this.emptystate.classList.remove(classes.hidden);
    this.loader.classList.remove(classes.loading);
    this.form.classList.add(classes.hidden);
    this.bottom.classList.add(classes.hidden);
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

const cartDrawer = {
  onLoad() {
    const isDrawerCart = this.container.querySelector(selectors.drawer);
    if (isDrawerCart) {
      this.cart = new CartItems(this);
    }

    const hasShipping = this.container.querySelector(selectors.shipping);
    if (hasShipping) {
      new ShippingCalculator(this);
    }
  },
  onUnload: function () {
    if (this.cart && typeof this.cart.unload === 'function') {
      this.cart.unload();
    }
  },
};

export {cartDrawer, CartItems};
